import { useEffect, useState } from 'react';
import './rewardcards.css';
import ScratchCard from './scratchcard';
import icon from '../../assets/icon.png';
import { revealCompletedJourneyReward } from '../../services/rewardService';

function ChevronLeftIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M15 5l-7 7 7 7" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function KebabIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="5" r="1.7" fill="#ffffff" />
      <circle cx="12" cy="12" r="1.7" fill="#ffffff" />
      <circle cx="12" cy="19" r="1.7" fill="#ffffff" />
    </svg>
  );
}

/* Tone-on-tone decoration that fills a card. `color` paints the shapes. */
function CardPattern({ color }) {
  return (
    <svg
      className="rewardcard-pattern"
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      aria-hidden="true"
    >
      <g fill={color} stroke={color}>
        {/* brand icon */}
        <image
          href={icon}
          x="28"
          y="31"
          width="36"
          height="36"
          opacity="0.9"
        />

        {/* shopping bag */}
        {/* <g transform="translate(60 55)">
          <path d="M2 8 L18 8 L20 26 L0 26 Z" opacity="0.9" />
          <path d="M6 8 a4 4 0 0 1 8 0" stroke={color} strokeWidth="2" fill="none" />
        </g> */}

        {/* trophy */}
        {/* <g transform="translate(14 58)">
          <path d="M4 0 h10 v4 a5 5 0 0 1 -10 0 Z" />
          <rect x="8" y="8" width="2" height="4" />
          <rect x="4" y="12" width="10" height="3" rx="1" />
          <path d="M4 1 h-3 a3 3 0 0 0 3 4 M14 1 h3 a3 3 0 0 1 -3 4" stroke={color} strokeWidth="1.6" fill="none" />
        </g> */}

        {/* rosette / ribbon */}
        {/* <g transform="translate(22 14)">
          <circle cx="8" cy="8" r="6" />
          <circle cx="8" cy="8" r="3" fill="#ffffff" fillOpacity="0.25" />
          <path d="M5 13 l-2 8 l5 -3 l5 3 l-2 -8 Z" />
        </g> */}

        {/* banknote */}
        {/* <rect x="66" y="20" width="20" height="12" rx="2" />
        <circle cx="76" cy="26" r="3" fill="#ffffff" fillOpacity="0.3" /> */}

        {/* scissor / firework burst */}
        {/* <g transform="translate(10 30)" stroke={color} strokeWidth="2" strokeLinecap="round">
          <path d="M0 12 L8 8 M1 6 L8 8 M3 1 L8 8 M9 0 L8 8 M14 3 L8 8" />
        </g> */}

        {/* scattered dots */}
        {/* <circle cx="50" cy="12" r="1.6" />
        <circle cx="88" cy="46" r="1.6" />
        <circle cx="42" cy="40" r="1.4" />
        <circle cx="30" cy="86" r="1.6" />
        <circle cx="70" cy="88" r="1.4" />
        <circle cx="90" cy="72" r="1.6" />
        <circle cx="6" cy="52" r="1.4" /> */}

        {/* little rotated squares */}
        {/* <rect x="52" y="34" width="3" height="3" transform="rotate(20 53 35)" />
        <rect x="84" y="60" width="3" height="3" transform="rotate(15 85 61)" />
        <rect x="18" y="46" width="3" height="3" transform="rotate(25 19 47)" /> */}

        {/* small arcs */}
        {/* <path d="M10 74 a4 4 0 0 1 6 0" stroke={color} strokeWidth="1.8" fill="none" />
        <path d="M78 78 a4 4 0 0 1 6 0" stroke={color} strokeWidth="1.8" fill="none" />
        <path d="M40 84 a3 3 0 0 1 5 0" stroke={color} strokeWidth="1.6" fill="none" />
        <path d="M58 8 a3 3 0 0 1 5 0" stroke={color} strokeWidth="1.6" fill="none" /> */}
      </g>
    </svg>
  );
}

const PATTERN_COLOR = {
  blue: '#0ea5e9',
  orange: '#0ea5e9',
  google: '#0ea5e9',
};

// The single reward belonging to the authenticated user's own backend-
// verified completed journey (Step 5G) is resolved by App.js and passed in
// as `reward` — never fetched or filtered here, and never a list of every
// active reward. Internally adapted to a one-item array so the existing
// grid layout below needs no structural changes; no additional/fake
// rewards are ever added to it.
// Formats a reward's wallet_credit exactly as the admin configured it —
// whole rupees with no decimals, fractional amounts with two. Mirrors
// scratchcard.js's formatWalletAmount so the amount reads identically
// across both reward screens.
function formatWalletAmount(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return `₹${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
}

export default function RewardCards({ onBack, onHome, total = '₹78.90', reward = null, isLoading = false }) {
  const [openId, setOpenId] = useState(null);

  // Source of truth is the backend's user_reward_reveals row, surfaced as
  // `reward.revealed` (Step 5I-C) on the same object App.js already loads
  // via getCompletedJourneyReward() — never assumed/guessed locally. Seeded
  // from it on mount and re-synced whenever a fresh `reward` arrives (e.g.
  // App.js's reward-loading effect refetching after view changes to
  // 'rewardcards', including right after the direct Points -> Reward ->
  // ScratchCard flow just persisted a reveal).
  const [revealedIds, setRevealedIds] = useState(() => (reward?.revealed ? [reward.id] : []));

  useEffect(() => {
    setRevealedIds(reward?.revealed ? [reward.id] : []);
  }, [reward]);

  const rewards = reward ? [reward] : [];
  const status = isLoading ? 'loading' : 'ready';

  const openCard = rewards.find((r) => r.id === openId) || null;

  const closeCard = () => setOpenId(null);

  const markRevealed = () => {
    setRevealedIds((ids) => (ids.includes(openId) ? ids : [...ids, openId]));
    setOpenId(null);

    // Persist server-side via the existing Step 5G endpoint — idempotent
    // (a repeat reveal for the same reward never creates a duplicate row),
    // so this is safe even though the direct Points -> Reward -> ScratchCard
    // flow may have already revealed it. The local state above already gave
    // the user immediate visual feedback; this just keeps Laravel as the
    // durable source of truth in case this card was opened unrevealed.
    revealCompletedJourneyReward().catch((err) => {
      console.error('RewardCards: failed to persist reward reveal.', err);
    });
  };

  // Opening a card replaces the grid with its scratch card; closing brings the grid back.
  if (openCard) {
    return <ScratchCard onBack={closeCard} onContinue={markRevealed} reward={openCard} />;
  }

  return (
    <div className="rewardcards-screen">
      <div className="rewardcards-frame">
        {/* <header className="rewardcards-header">
          <button
            type="button"
            className="rewardcards-icon-btn"
            aria-label="Go back"
            onClick={onBack}
          >
            <ChevronLeftIcon />
          </button>
          <h1 className="rewardcards-header-title">{total} total rewards</h1>
          <button type="button" className="rewardcards-icon-btn" aria-label="More options">
            <KebabIcon />
          </button>
        </header> */}

        <div className="rewardcards-body">
          <h2 className="rewardcards-heading">Your rewards</h2>

          {status === 'loading' && (
            <p className="rewardcards-status">Loading rewards…</p>
          )}

          {status !== 'loading' && rewards.length === 0 && (
            <p className="rewardcards-status">No rewards available yet.</p>
          )}

          {status !== 'loading' && rewards.length > 0 && (
            <div className="rewardcards-grid">
              {rewards.map((r) => {
                const revealed = revealedIds.includes(r.id);
                const variant = r.variant || 'blue';
                return (
                  <button
                    type="button"
                    key={r.id}
                    className={'rewardcard rewardcard-' + variant + (revealed ? ' rewardcard-revealed' : '')}
                    onClick={() => setOpenId(r.id)}
                  >
                    <CardPattern color={PATTERN_COLOR[variant]} />

                    {revealed && (
                      <span className="rewardcard-revealed-content">
                        <img src={icon} alt="" className="rewardcard-revealed-icon" />
                        <span className="rewardcard-revealed-text">
                          <span className="rewardcard-revealed-discount">{r.discount}</span>
                          {formatWalletAmount(r.wallet_credit) && (
                            <span className="rewardcard-revealed-amount">
                              {formatWalletAmount(r.wallet_credit)}
                            </span>
                          )}
                          <span className="rewardcard-revealed-desc">{r.description}</span>
                        </span>
                      </span>
                    )}

                    {revealed ? (
                      <span className="rewardcard-badge rewardcard-badge-done">Opened</span>
                    ) : (
                      r.badge && (
                        <span
                          className={
                            'rewardcard-badge' +
                            (r.badgeVariant === 'new' ? ' rewardcard-badge-new' : '')
                          }
                        >
                          {r.badge}
                        </span>
                      )
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <button type="button" className="rewardcards-home-btn" onClick={onHome}>
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}
